import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Order, SortParams } from "@/types/types";

import { initialPageSize } from "@/utils/preferredPageSize";

import { IFilterWithValues } from "../../List/components/FilterModal";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 15;
const SEARCH_DEBOUNCE_MS = 800;

interface UseContactsUrlStateOptions {
	prefix: string;
	defaultSortBy: string;
	defaultSortOrder: Order;
	withSearch?: boolean;
}

export const useContactsUrlState = ({
	prefix,
	defaultSortBy,
	defaultSortOrder,
	withSearch = false,
}: UseContactsUrlStateOptions) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const paramKey = useCallback((key: string) => `${prefix}_${key}`, [prefix]);

	// ── Read initial values from URL ─────────────────────────────────────
	const initialPage = Number(searchParams.get(paramKey("page"))) || DEFAULT_PAGE;
	const initialSortBy = searchParams.get(paramKey("sortBy")) || defaultSortBy;
	const initialSortOrder = (searchParams.get(paramKey("sortOrder")) as Order) || defaultSortOrder;
	const initialSearch = searchParams.get(paramKey("search")) || "";
	const initialSemanticProspects = searchParams.get(paramKey("semanticProspects")) || "";
	const initialSemanticOrganizations = searchParams.get(paramKey("semanticOrganizations")) || "";

	let initialFilter: IFilterWithValues | undefined;

	try {
		const raw = searchParams.get(paramKey("filter"));

		if (raw) {
			initialFilter = JSON.parse(raw);
		}
	} catch {
		initialFilter = undefined;
	}

	// ── State ────────────────────────────────────────────────────────────
	const [page, setPageState] = useState(initialPage);
	// A URL limit wins; otherwise the size the user last picked in any table (PREC-4403). The URL
	// omits the default size, so a later page without one reads as the default.
	const [limit, setLimitState] = useState(() =>
		initialPageSize({
			urlLimit: searchParams.get(paramKey("limit")),
			urlPage: searchParams.get(paramKey("page")),
			fallback: DEFAULT_LIMIT,
		})
	);
	const [sortingInbox, setSortingInboxState] = useState<SortParams>({
		sortBy: initialSortBy,
		sortOrder: initialSortOrder,
	});
	const [filter, setFilterState] = useState<IFilterWithValues | undefined>(initialFilter);
	const [searchValue, setSearchValue] = useState(initialSearch);
	const [query, setQuery] = useState(initialSearch);
	const [isTyping, setIsTyping] = useState(false);
	const [semanticSearchProspects, setSemanticSearchProspectsState] =
		useState(initialSemanticProspects);
	const [semanticSearchOrganizations, setSemanticSearchOrganizationsState] = useState(
		initialSemanticOrganizations
	);

	// ── Sync state → URL ─────────────────────────────────────────────────
	const syncToUrl = useCallback(
		(updates: Record<string, string | undefined>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					for (const [key, value] of Object.entries(updates)) {
						if (value === undefined || value === "") {
							next.delete(key);
						} else {
							next.set(key, value);
						}
					}

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const buildUrlUpdates = useCallback(
		(
			p: number,
			l: number,
			sort: SortParams,
			f: IFilterWithValues | undefined,
			q: string,
			semProspects?: string,
			semOrganizations?: string
		): Record<string, string | undefined> => {
			const updates: Record<string, string | undefined> = {};

			updates[paramKey("page")] = p !== DEFAULT_PAGE ? String(p) : undefined;
			updates[paramKey("limit")] = l !== DEFAULT_LIMIT ? String(l) : undefined;
			updates[paramKey("sortBy")] = sort.sortBy !== defaultSortBy ? sort.sortBy : undefined;
			updates[paramKey("sortOrder")] =
				sort.sortOrder !== defaultSortOrder ? sort.sortOrder : undefined;
			updates[paramKey("filter")] = f && Object.keys(f).length > 0 ? JSON.stringify(f) : undefined;

			if (withSearch) {
				updates[paramKey("search")] = q || undefined;
			}

			updates[paramKey("semanticProspects")] = semProspects || undefined;
			updates[paramKey("semanticOrganizations")] = semOrganizations || undefined;

			return updates;
		},
		[paramKey, defaultSortBy, defaultSortOrder, withSearch]
	);

	// ── Setters that sync to URL ─────────────────────────────────────────
	const setPage = useCallback(
		(p: number) => {
			setPageState(p);
			syncToUrl(
				buildUrlUpdates(
					p,
					limit,
					sortingInbox,
					filter,
					query,
					semanticSearchProspects,
					semanticSearchOrganizations
				)
			);
		},
		[
			limit,
			sortingInbox,
			filter,
			query,
			semanticSearchProspects,
			semanticSearchOrganizations,
			syncToUrl,
			buildUrlUpdates,
		]
	);

	const setLimit = useCallback(
		(l: number) => {
			setLimitState(l);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(
					DEFAULT_PAGE,
					l,
					sortingInbox,
					filter,
					query,
					semanticSearchProspects,
					semanticSearchOrganizations
				)
			);
		},
		[
			sortingInbox,
			filter,
			query,
			semanticSearchProspects,
			semanticSearchOrganizations,
			syncToUrl,
			buildUrlUpdates,
		]
	);

	const setSortingInbox = useCallback(
		(sort: SortParams) => {
			setSortingInboxState(sort);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(
					DEFAULT_PAGE,
					limit,
					sort,
					filter,
					query,
					semanticSearchProspects,
					semanticSearchOrganizations
				)
			);
		},
		[
			limit,
			filter,
			query,
			semanticSearchProspects,
			semanticSearchOrganizations,
			syncToUrl,
			buildUrlUpdates,
		]
	);

	const setFilter = useCallback(
		(f: IFilterWithValues | undefined) => {
			setFilterState(f);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(
					DEFAULT_PAGE,
					limit,
					sortingInbox,
					f,
					query,
					semanticSearchProspects,
					semanticSearchOrganizations
				)
			);
		},
		[
			limit,
			sortingInbox,
			query,
			semanticSearchProspects,
			semanticSearchOrganizations,
			syncToUrl,
			buildUrlUpdates,
		]
	);

	const setSemanticSearchProspects = useCallback(
		(value: string) => {
			setSemanticSearchProspectsState(value);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(
					DEFAULT_PAGE,
					limit,
					sortingInbox,
					filter,
					query,
					value,
					semanticSearchOrganizations
				)
			);
		},
		[limit, sortingInbox, filter, query, semanticSearchOrganizations, syncToUrl, buildUrlUpdates]
	);

	const setSemanticSearchOrganizations = useCallback(
		(value: string) => {
			setSemanticSearchOrganizationsState(value);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(
					DEFAULT_PAGE,
					limit,
					sortingInbox,
					filter,
					query,
					semanticSearchProspects,
					value
				)
			);
		},
		[limit, sortingInbox, filter, query, semanticSearchProspects, syncToUrl, buildUrlUpdates]
	);

	// ── Combined setter for filter + semantic search (avoids race conditions) ──
	const applyFilters = useCallback(
		(opts: {
			filter?: IFilterWithValues | undefined;
			semanticSearchProspects?: string;
			semanticSearchOrganizations?: string;
		}) => {
			const newFilter = opts.filter;
			const newSemProspects =
				opts.semanticSearchProspects !== undefined
					? opts.semanticSearchProspects
					: semanticSearchProspects;
			const newSemOrganizations =
				opts.semanticSearchOrganizations !== undefined
					? opts.semanticSearchOrganizations
					: semanticSearchOrganizations;

			setFilterState(newFilter);
			setSemanticSearchProspectsState(newSemProspects);
			setSemanticSearchOrganizationsState(newSemOrganizations);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(
					DEFAULT_PAGE,
					limit,
					sortingInbox,
					newFilter,
					query,
					newSemProspects,
					newSemOrganizations
				)
			);
		},
		[
			limit,
			sortingInbox,
			query,
			semanticSearchProspects,
			semanticSearchOrganizations,
			syncToUrl,
			buildUrlUpdates,
		]
	);

	// ── Search debounce (only when withSearch is true) ────────────────────
	const searchTimer = useRef<NodeJS.Timeout | null>(null);
	const isInitialMount = useRef(true);

	useEffect(() => {
		if (!withSearch) {
			return;
		}

		// Skip the first run so we don't reset page on mount / reload
		if (isInitialMount.current) {
			isInitialMount.current = false;

			return;
		}

		setIsTyping(true);

		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}

		searchTimer.current = setTimeout(() => {
			setQuery(searchValue);
			setIsTyping(false);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(
					DEFAULT_PAGE,
					limit,
					sortingInbox,
					filter,
					searchValue,
					semanticSearchProspects,
					semanticSearchOrganizations
				)
			);
		}, SEARCH_DEBOUNCE_MS);

		return () => {
			if (searchTimer.current) {
				clearTimeout(searchTimer.current);
			}
		};
	}, [searchValue]);

	return {
		page,
		setPage,
		limit,
		setLimit,
		sortingInbox,
		setSortingInbox,
		filter,
		setFilter,
		applyFilters,
		semanticSearchProspects,
		setSemanticSearchProspects,
		semanticSearchOrganizations,
		setSemanticSearchOrganizations,
		...(withSearch && {
			searchValue,
			setSearchValue,
			query,
			isTyping,
		}),
	};
};
